import { AxiosResponse } from 'axios';

export type IVersionHistory = {
    title: string;
    versions: ICoachingFormVersions[];
};

export type ICoachingFormVersions = {
    id: number;
    user: string;
    updatedAt: string;
};

// TODO: Expand changes as needed together with backend
export type ICoachingFormChange =
    | 'Title'
    | 'SectionsOrder'
    | 'QuestionsOrder'
    | 'SectionAdded'
    | 'SectionRemoved'
    | 'QuestionAdded'
    | 'QuestionRemoved'
    | 'Other';

export enum CoachingFormAnswerType {
    Affirmative = 'Affirmative',
    Interval = 'Interval',
    Custom = 'Custom',
    Text = 'Text',
}

export type ICoachingFormQuestion = {
    id: string;
    title: string;
    description: string;
    prompt: string;
    useComments?: boolean;
    notApplicable?: boolean;
    notApplicableLabel?: string;
    isCritical: boolean;
    answerType: CoachingFormAnswerType;
    answers: ICoachingFormAnswer[];
    interval?: ICoachingFormQuestionInterval;
};

export type ICoachingFormAnswer = {
    id: string;
    label: string;
    points: number;
    notApplicable?: boolean;
};

export type ICoachingFormQuestionInterval = {
    from: number;
    to: number;
    step: number;
};

export type ICoachingFormProtocolsUsage = {
    numberOfProtocols: number;
    lastUsedAt: Date | null;
};

export type ICoachingFormSection = {
    id: string;
    title: string;
    useComments?: boolean;
    questions: ICoachingFormQuestion[];
};

export type ICoachingFormOpenComment = {
    title?: string;
    description?: string;
};

export type ICoachingFormRestrictedComment = {
    title?: string;
    description?: string;
};

export type ICoachingFormReasonCode = {
    id: string;
    parentId?: string;
    code: string;
    childCodes?: ICoachingFormReasonCode[];
};

export type ICoachingFormReasonCodes = {
    title: string;
    threshold: number;
    description?: string;
    reasonCodeDescription?: string;
    codes: ICoachingFormReasonCode[];
};

export type ICoachingFormApprovalRating = {
    id: string;
    name: string;
    minPoints: number;
    maxPoints: number;
    requireApprovalBeforeDisplay: boolean;
};

export type ICoachingFormApprovalRatings = {
    criticalQuestionFallbackRatingId: string;
    ratings: ICoachingFormApprovalRating[];
};

export type ICoachingForm = {
    id: string;
    title: string;

    useAi: boolean;
    useAction: boolean;
    useReasonCodes: boolean;
    useApprovalRating: boolean;
    useOpenComment: boolean;
    useRestrictedComment: boolean;
    isActive: boolean;
    language: string;

    prompt?: string;
    action?: string;
    reasonCodes?: ICoachingFormReasonCodes;
    approvalRatings?: ICoachingFormApprovalRatings;
    openComment?: ICoachingFormOpenComment;
    restrictedComment?: ICoachingFormRestrictedComment;

    sections: ICoachingFormSection[];
};

export type ICoachingFormTableItem = Pick<ICoachingForm, 'id' | 'title' | 'useAi' | 'isActive'> & {
    createdAt: string;
    createdBy: string;
    updatedBy: string;
};

export type ICoachingFormReasonCodeNested = {
    code: string;
    childCodes?: string[];
};

export type RequestGetCoachingForm = {
    id: string;
};

export type RequestPostCoachingForm = Omit<
    ICoachingForm,
    'id' | 'approvalRatings' | 'reasonCodes'
> & {
    approvalRatings?: Omit<ICoachingFormApprovalRatings, 'criticalQuestionFallbackRatingId'> & {
        criticalQuestionFallbackRatingName?: string;
    };

    reasonCodes?: Omit<ICoachingFormReasonCodes, 'codes'> & {
        codes?: ICoachingFormReasonCodeNested[];
    };
};

export type RequestPostCoachingFormRevision = RequestPostCoachingForm & {
    formId: string;
    changes: ICoachingFormChange[];
};

export type ResponseGetCoachingForm = Promise<ICoachingForm>;

export type RequestDeleteCoachingForm = {
    id: string;
};

export type RequestUpdateCoachingFormInactiveState = {
    id: string;
    isInactive: boolean;
};

export type ResponseDeleteCoachingForm = Promise<AxiosResponse<void>>;

export type ResponseGetCoachingFormTable = {
    forms: ICoachingFormTableItem[];
};
